import React, { useEffect, useState } from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableBody,
  TablePagination,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  GetUserInfo,
  PutUserInfo,
  SortOrder,
} from '../../../services/apiTypes';
import {
  LANGUAGE,
  ROLE,
  ROWS_PER_PAGE_LIST,
  ORDER_TEXT,
} from '../../../_constants/Const';
import {
  ADD_BUTTON_SIZE,
  TABLE_HEADER,
  TEXT_OVERFLOW_1LINE,
} from '../../../_constants/styleConst';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import ConfirmModal from '../../../_common/Modal/ConfirmModal';
import { UserSearchParam } from '../../../stores/userManagement/api';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LockOpenIcon from '@mui/icons-material/LockOpen';

/** ソートKeyの型 */
export type SortKeyType = (typeof adminColumns)[number]['sortKey'];

type UsersTableProps = {
  params: UserSearchParam;
  isAdmin: boolean;
  handleDialogUpdateOpen: (userId: string) => void;
  handleModalAccountUnlockOpen: (user: GetUserInfo) => void;
  userList: GetUserInfo[];
  onSortChange: (sortKey: SortKeyType, order: SortOrder) => void;
  handleUserDelete: (userId: string) => void;
};

/** メ内サ権限で表示するカラム */
const adminColumns = [
  { id: 0, width: '15%', sortKey: 'UserId', label: 'LBL_USER_ID' },
  { id: 1, width: '15%', sortKey: 'UserName', label: 'LBL_NAME' },
  { id: 2, width: '12%', sortKey: 'AuthorityId', label: 'LBL_ROLE' },
  { id: 3, width: '30%', sortKey: 'CompanyKey', label: 'LBL_COMPANY' },
  { id: 4, width: '12%', sortKey: 'LangCode', label: 'LBL_LANGUAGE' },
  { id: 5, width: '10%', sortKey: undefined, label: 'LBL_ACCOUNT_UNLOCK' },
  { id: 6, width: '3%', sortKey: undefined, label: 'LBL_EDIT' },
  { id: 7, width: '3%', sortKey: undefined, label: 'LBL_DELETE' },
] as const;

/** 地域管理者権限で表示するカラム */
const managerColumns = [
  { id: 0, width: '16%', sortKey: 'UserId', label: 'LBL_USER_ID' },
  { id: 1, width: '16%', sortKey: 'UserName', label: 'LBL_NAME' },
  { id: 2, width: '13%', sortKey: 'AuthorityId', label: 'LBL_ROLE' },
  { id: 3, width: '33%', sortKey: 'CompanyKey', label: 'LBL_COMPANY' },
  { id: 4, width: '12%', sortKey: 'LangCode', label: 'LBL_LANGUAGE' },
  { id: 5, width: '10%', sortKey: undefined, label: 'LBL_ACCOUNT_UNLOCK' },
] as const;

const UsersTable: React.FC<UsersTableProps> = ({
  params,
  isAdmin,
  userList,
  handleDialogUpdateOpen,
  handleModalAccountUnlockOpen,
  onSortChange,
  handleUserDelete,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_LIST[0]);

  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  /** ユーザーの変更情報を取る */
  const [targetUser, setTargetUser] = useState<PutUserInfo>();

  /** 権限に応じて制限をかける（メ内サのみが変更・削除できる） */
  const usersTableColumnsByPermissions = isAdmin
    ? adminColumns
    : managerColumns;

  useEffect(() => {
    setPage(0);
  }, [userList]);

  const handleSortClick = (sortKey: SortKeyType) => {
    if (sortKey !== undefined) {
      let order: SortOrder = ORDER_TEXT.DESC; // デフォルト降順で設定
      if (sortKey === params.sortKey) {
        order =
          params.order === ORDER_TEXT.DESC ? ORDER_TEXT.ASC : ORDER_TEXT.DESC;
      }
      if (order === ORDER_TEXT.ASC) {
        onSortChange(sortKey, ORDER_TEXT.ASC);
      } else {
        onSortChange(sortKey, ORDER_TEXT.DESC);
      }
    }
  };

  const handleOpenConfirmDeleteModal = (user: PutUserInfo) => {
    setTargetUser(user);
    setIsOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (targetUser) handleUserDelete(targetUser.userId);
    setIsOpenDeleteModal(false);
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const isDownwardIcon = (sortKey: SortKeyType) => {
    if (sortKey === params.sortKey) {
      return params.order === ORDER_TEXT.DESC ? true : false;
    }
    return false;
  };

  const isUpwardIcon = (sortKey: SortKeyType) => {
    if (sortKey === params.sortKey) {
      return params.order === ORDER_TEXT.ASC ? true : false;
    }
    return false;
  };

  return (
    <Box ml="2%" mr="2%" mb={`calc(${ADD_BUTTON_SIZE} * 1.5)`}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {[...usersTableColumnsByPermissions]
                .sort((a, b) => a.id - b.id)
                .map((column, i) => (
                  <TableCell
                    key={`user_header_${i}`}
                    sx={{
                      ...TABLE_HEADER,
                      width: column.width,
                      cursor: column.sortKey ? 'pointer' : 'default',
                    }}
                    onClick={() => handleSortClick(column.sortKey)}
                  >
                    <Box display="flex" alignItems="center">
                      {t(column.label)}
                      {isDownwardIcon(column.sortKey) && (
                        <ArrowDownwardIcon
                          style={{ fontSize: 20, left: '5px' }}
                        />
                      )}
                      {isUpwardIcon(column.sortKey) && (
                        <ArrowUpwardIcon
                          style={{ fontSize: 20, left: '5px' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {userList
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user, i) => (
                <TableRow key={`user_master_${i}`} hover>
                  {/* ユーザーID */}
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2">{user.userId}</Typography>
                    </Box>
                  </TableCell>
                  {/* 名前 */}
                  <TableCell>
                    <Typography variant="body2">{user.name}</Typography>
                  </TableCell>
                  {/* 役割 */}
                  <TableCell sx={{ minWidth: '140px' }}>
                    <Typography variant="body2">
                      {t(ROLE[Number(user.roleId)])}
                    </Typography>
                  </TableCell>
                  {/* 会社キー */}
                  <TableCell sx={{ maxWidth: '600px' }}>
                    <Typography variant="body2" sx={TEXT_OVERFLOW_1LINE}>
                      {accountsInfoData.companyList.find(
                        (item) => item.key === user.companyKey,
                      )?.name ?? user.companyKey}
                    </Typography>
                  </TableCell>
                  {/* 言語 */}
                  <TableCell>
                    <Typography variant="body2" sx={{ minWidth: '80px' }}>
                      {t(LANGUAGE[user.language])}
                    </Typography>
                  </TableCell>
                  {/* アカウントロック(ロック中のみアイコン表示) */}
                  <TableCell sx={{ minWidth: '120px' }}>
                    {user.isLocked && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Typography variant="body2">
                          <IconButton size="small">
                            <LockOpenIcon
                              fontSize="small"
                              onClick={() => handleModalAccountUnlockOpen(user)}
                            />
                          </IconButton>
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  {/* 変更と削除（メ内サのみ） */}
                  {isAdmin && (
                    <>
                      <TableCell>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Typography variant="body2">
                            <IconButton size="small">
                              <Edit
                                fontSize="small"
                                onClick={() =>
                                  handleDialogUpdateOpen(user.userId)
                                }
                              />
                            </IconButton>
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Typography variant="body2">
                            <IconButton size="small">
                              <Delete
                                color="error"
                                fontSize="small"
                                onClick={() => {
                                  handleOpenConfirmDeleteModal({
                                    ...user,
                                    password: '',
                                  });
                                }}
                              />
                            </IconButton>
                          </Typography>
                        </Box>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          labelRowsPerPage={t('LBL_ROWS_PER_PAGE')}
          rowsPerPageOptions={ROWS_PER_PAGE_LIST}
          component="div"
          count={userList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            t('LBL_DISPLAY_DATA_LABEL', { 0: from, 1: to, 2: count })
          }
        />
      </TableContainer>
      <ConfirmModal
        isOpen={isOpenDeleteModal}
        displayMessage={t('MSG_DELETE_USER')}
        onClickCancel={() => setIsOpenDeleteModal(false)}
        onClickOk={handleConfirmDelete}
      />
    </Box>
  );
};

export default UsersTable;
